import { Icon } from "@iconify/react";
import { useTheme } from "../../../hooks/useTheme";
import { Colors } from "../../../utils/colors";
import { Event, TicketV2, AccountData } from "@markit/common.types";
import { useCallback, useMemo, useState } from "react";
import { API } from "../../../API";
import PopupModalContainer from "../../Containers/PopupModalContainer";
import RectangleButton from "../../Buttons/RectangleButton";
import { useSelector } from "react-redux";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import { useLiveUpdatingEventAnalytics } from "../../../hooks/useLiveUpdatingEventAnalytics";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";
import { getUserData } from "../../../utils/FirebaseUtils";
import { getAccountState } from "../../../redux/slices/accountSlice";
import ConfirmActionModal from "../../Containers/ConfirmPopups/ConfirmActionModal";
import StandardBorderedContainer from "../../Containers/StandardBorderedContainer";
import {
  getStripeAccountBalance,
  testStripe,
} from "../../../utils/stripeAccountUtils";
import CustomCheckbox from "../../CustomCheckbox";
import { CircularProgress, Skeleton } from "@mui/material";
import {
  isProductSpecificType,
  MARKIT_TERMS_OF_USE,
  productItemLabel,
} from "@markit/common.utils";
import { FormatCurrency } from "../../../utils/FormatCurrency";
import TextHoverButton from "../../Buttons/TextHoverButton";
import AddStripeFundsModal from "../../Stripe/AddStripeFundsModal";
import AlertContainer from "../../Containers/AlertContainer";

type EventRefundModalProps = {
  event: Event;
  setModalVisible: (modalVisible: boolean) => void;
  // For single ticket refund
  ticket?: TicketV2;
  userData?: AccountData;
};

export const EventRefundModal = (props: EventRefundModalProps) => {
  const { event, setModalVisible, ticket, userData } = props;
  const { theme } = useTheme();
  const { accountData } = useSelector(getAccountState).account;
  const [baseTicketCost, setBaseTicketCost] = useState(0);
  const [ticketFees, setTicketFees] = useState(0);
  const [isMainTicket, setIsMainTicket] = useState(false);
  const [refundFeesChecked, setRefundFeesChecked] = useState(false);
  const [originalOwner, setOriginalOwner] = useState<AccountData>();
  const [currentOwner, setCurrentOwner] = useState<AccountData>();
  const [loading, setLoading] = useState(true);
  const [loadingRefund, setLoadingRefund] = useState(false);
  const [currentBalance, setCurrentBalance] = useState<number | null>(null);
  const [showRefundResult, setShowRefundResult] = useState(false);
  const [isSuccessfulyRefunded, setIsSuccessfulyRefunded] = useState(false);
  const [confirmRefundVisible, setConfirmRefundVisible] = useState(false);
  const [addFundsModal, setAddFundsModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);

  const styles = {
    sectionContainer: {
      backgroundColor: Colors.WHITE1,
      borderRadius: 14,
      paddingBlock: 14,
    },
  };

  const { uniqueGuestsUids } = useLiveUpdatingEventAnalytics({
    eventId: event.id,
  });

  const isProductSpecific = useMemo(
    () => isProductSpecificType(event.eventType),
    [event.eventType]
  );

  const retrieveStripeBalance = useCallback(async () => {
    setCurrentBalance(null);
    const stripeAccountId = testStripe(accountData.uid)
      ? accountData.testStripeAccountId || ""
      : accountData.stripeAccountId;
    const { balance } = await getStripeAccountBalance(
      accountData.uid,
      stripeAccountId || ""
    );
    setCurrentBalance(balance);
  }, [
    accountData.uid,
    accountData.stripeAccountId,
    accountData.testStripeAccountId,
  ]);

  useAsyncOnMount(async () => {
    let baseTicketCost = 0;
    let ticketFees = 0;
    if (userData && ticket) {
      // Single ticket refund price
      const [response, originalOwnerData, currentOwnerData] = await Promise.all(
        [
          testStripe(accountData.uid)
            ? API.testPayment.getTicketPrice({
                eventId: event.id,
                ticket,
              })
            : API.payment.getTicketPrice({
                eventId: event.id,
                ticket,
              }),
          getUserData(ticket.originalOwner),
          getUserData(ticket.uid),
        ]
      );

      const { costOfTicket, costOfFee, isMainTicket } = response;
      baseTicketCost = costOfTicket;
      ticketFees = costOfFee;
      setIsMainTicket(isMainTicket);
      if (originalOwnerData) {
        setOriginalOwner(originalOwnerData);
      }
      if (currentOwnerData) {
        setCurrentOwner(currentOwnerData);
      }
    } else {
      // Full event refund total price
      const response = await (
        testStripe(accountData.uid) ? API.testPayment : API.payment
      ).getTotalRevenue({ eventId: event.id });
      const { totalRevenue, totalFees } = response;
      // Rate limiting error due to too many Stripe requests, contact support
      if (totalRevenue === -1) {
        setShowAlertModal(true);
        return;
      }
      baseTicketCost = totalRevenue;
      ticketFees = totalFees;
    }

    setBaseTicketCost(baseTicketCost);
    setTicketFees(ticketFees);

    await retrieveStripeBalance();
    setLoading(false);
  });

  const ticketCost = useMemo(
    () => baseTicketCost + (refundFeesChecked ? ticketFees : 0),
    [baseTicketCost, refundFeesChecked, ticketFees]
  );

  const ticketCostDollars = useMemo(
    () => "$" + (ticketCost / 100).toFixed(2),
    [ticketCost]
  );

  const ticketFeesDollars = useMemo(
    () => "$" + (ticketFees / 100).toFixed(2),
    [ticketFees]
  );

  const canRefund = useMemo(
    () => currentBalance && currentBalance >= ticketCost,
    [currentBalance, ticketCost]
  );

  const itemLabel = useMemo(
    () => productItemLabel(event.eventType),
    [event.eventType]
  );

  const isFlexiblePricingTicket = useMemo(() => {
    const customTicket = event.customTickets.find(
      (customTicket) => customTicket.id === ticket?.customTicketId
    );
    if (customTicket) {
      return customTicket.flexiblePricing;
    }
    return false;
  }, [event.customTickets, ticket?.customTicketId]);

  const refundPaymentOnPress = useCallback(async () => {
    try {
      setLoadingRefund(true);
      if (ticket) {
        await (
          testStripe(accountData.uid) ? API.testPayment : API.payment
        ).refundPayment({
          hostId: event.createdBy,
          userId: ticket.originalOwner,
          eventId: event.id,
          ticket,
          ticketCost,
        });
        setIsSuccessfulyRefunded(true);
      }
    } catch (e) {
      console.log("Error Message: ", e);
      setIsSuccessfulyRefunded(false);
    }
    setShowRefundResult(true);
    setLoadingRefund(false);
  }, [accountData.uid, event.createdBy, event.id, ticket, ticketCost]);

  const refundUsersOnPress = useCallback(async () => {
    try {
      setLoadingRefund(true);
      const response = await (
        testStripe(accountData.uid) ? API.testPayment : API.payment
      ).refundUsers({
        hostId: event.createdBy,
        attendeeIds: uniqueGuestsUids,
        eventId: event.id,
        includeFees: refundFeesChecked,
      });
      const { success } = response;
      if (!success) {
        setShowAlertModal(true);
        return;
      }
      setIsSuccessfulyRefunded(true);
    } catch (e) {
      console.log("Error Message: ", e);
      setIsSuccessfulyRefunded(false);
    }
    setShowRefundResult(true);
    setLoadingRefund(false);
  }, [
    accountData.uid,
    event.createdBy,
    event.id,
    uniqueGuestsUids,
    refundFeesChecked,
  ]);

  const renderRefundDetails = useMemo(
    () => (
      <div className="ColumnNormal" style={{ paddingInline: 14, gap: 4 }}>
        <span className="bodyMedium">Refund Details</span>
        {ticket && isMainTicket ? (
          <span className="smallBodySubtext">Main {itemLabel.singularCap}</span>
        ) : null}
        {originalOwner && currentOwner ? (
          <>
            <div className="AlignedRowSpaced" style={{ gap: 14 }}>
              <div style={{ flex: 1 }}>
                <span className="bodySubtext" style={{ whiteSpace: "nowrap" }}>
                  Current {itemLabel.singularCap} Owner
                </span>
              </div>
              <span className="bodySubtext" style={{ textAlign: "right" }}>
                {currentOwner.fullName}
              </span>
            </div>
            {originalOwner.uid !== currentOwner.uid ? (
              <div className="AlignedRowSpaced" style={{ gap: 14 }}>
                <span className="bodySubtext" style={{ whiteSpace: "nowrap" }}>
                  Original {itemLabel.singularCap} Owner
                </span>
                <span className="bodySubtext" style={{ textAlign: "right" }}>
                  {originalOwner.fullName}
                </span>
              </div>
            ) : null}
            <div className="AlignedRowSpaced">
              <span style={{ fontSize: 14, color: Colors.GRAY1 }}>
                {itemLabel.singularCap} Price
              </span>
              <span className="bodySubtext">{ticketCostDollars}</span>
            </div>
          </>
        ) : (
          <div className="AlignedRowSpaced">
            <span style={{ fontSize: 14, color: Colors.GRAY1 }}>
              Full {itemLabel.entityCap} Refund Amount
            </span>
            <span className="bodySubtext">{ticketCostDollars}</span>
          </div>
        )}
      </div>
    ),
    [
      ticket,
      isMainTicket,
      currentOwner,
      originalOwner,
      ticketCostDollars,
      itemLabel.entityCap,
      itemLabel.singularCap,
    ]
  );

  const renderRefundResult = useMemo(
    () => (
      <div className="ColumnNormal">
        <StandardBorderedContainer containerStyles={styles.sectionContainer}>
          {renderRefundDetails}
          <HorizontalDivider altMargin={14} />
          <div className="AlignedRowSpaced" style={{ paddingInline: 14 }}>
            <div className="AlignedRow" style={{ gap: 7 }}>
              <Icon
                icon={
                  isSuccessfulyRefunded
                    ? "ion:checkmark-circle"
                    : "ion:close-circle"
                }
                height={18}
                style={{
                  color: isSuccessfulyRefunded ? Colors.GREEN2 : Colors.RED3,
                }}
              />
              <span
                className="bodyMedium"
                style={{
                  color: isSuccessfulyRefunded ? Colors.GREEN2 : Colors.RED3,
                }}
              >
                {isSuccessfulyRefunded ? "Refund Successful" : "Refund Failed"}
              </span>
            </div>
            <span
              className="bodyMedium"
              style={{
                color: isSuccessfulyRefunded ? Colors.GREEN2 : Colors.RED3,
              }}
            >
              {ticketCostDollars}
            </span>
          </div>
        </StandardBorderedContainer>
      </div>
    ),
    [
      renderRefundDetails,
      isSuccessfulyRefunded,
      ticketCostDollars,
      styles.sectionContainer,
    ]
  );

  if (showAlertModal) {
    return (
      <AlertContainer
        headerComp="Unable to Refund Event"
        subHeaderComp="Due to the number of tickets sold, you must contact support to handle issuing refunds."
        closeModal={() => setModalVisible(false)}
        hideModal={!showAlertModal}
      />
    );
  }

  return (
    <>
      <PopupModalContainer
        headerComp={
          showRefundResult
            ? isSuccessfulyRefunded
              ? "Refund Successful"
              : "Refund Failed"
            : ticket
              ? `Refund ${itemLabel.singularCap}`
              : `Refund ${itemLabel.entityCap}`
        }
        closeOnOutsidePress={
          !confirmRefundVisible && !showRefundResult && !addFundsModal
        }
        noExit={true}
        valueComp={
          loading ? (
            <div
              className="Centering"
              style={{ margin: "auto", height: "30vh" }}
            >
              <CircularProgress style={{ color: Colors.GRAY1 }} size={24} />
            </div>
          ) : showRefundResult ? (
            renderRefundResult
          ) : (
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <StandardBorderedContainer
                containerStyles={styles.sectionContainer}
              >
                {renderRefundDetails}
                <HorizontalDivider altMargin={14} />
                <div
                  className="ColumnNormal"
                  style={{ paddingInline: 14, gap: 2 }}
                >
                  <div className="AlignedRowSpaced">
                    <span className="bodySubtext">
                      Available Stripe Balance
                    </span>
                    <span className="bodySubtext">
                      {currentBalance !== null ? (
                        FormatCurrency(currentBalance / 100)
                      ) : (
                        <Skeleton width={50} />
                      )}
                    </span>
                  </div>
                  {!loading && !canRefund ? (
                    <div className="AlignedRowSpaced">
                      <TextHoverButton
                        text="Add Funds"
                        onPress={() => setAddFundsModal(true)}
                        textStyles={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: Colors.BLUE5,
                        }}
                      />
                      <TextHoverButton
                        text="Refresh"
                        onPress={retrieveStripeBalance}
                        icon={
                          <Icon
                            icon="ion:sync-sharp"
                            height={13}
                            style={{ color: Colors.BLUE5 }}
                          />
                        }
                        textStyles={{ color: Colors.BLUE5 }}
                        disabled={currentBalance === null}
                      />
                    </div>
                  ) : null}
                </div>
                <HorizontalDivider altMargin={14} />
                <div
                  className="ColumnNormal"
                  style={{ paddingInline: 14, gap: 14 }}
                >
                  {userData && ticket ? (
                    <span className="smallBodySubtext">
                      <span style={{ color: Colors.BLACK }}>Note:</span>{" "}
                      Refunding a purchase will also remove the{" "}
                      {itemLabel.singular} from the current owner. If this was a
                      transferred {itemLabel.singular}, the refund will be
                      credited to the payment method of the original purchaser.
                    </span>
                  ) : (
                    <span className="smallBodySubtext">
                      <span style={{ color: Colors.BLACK }}>Note:</span> Refund
                      every purchase for this {itemLabel.entity}. This action
                      cannot be undone and will also remove all the{" "}
                      {itemLabel.plural} from the {itemLabel.entity}. The
                      refunds will be credited to the payment methods of the
                      original purchasers.
                    </span>
                  )}
                  {isFlexiblePricingTicket ? (
                    <span className="smallBodySubtext">
                      This {itemLabel.singular} has flexible pricing. Refunding
                      will remove all other flexible price {itemLabel.plural}{" "}
                      associated with this charge.
                    </span>
                  ) : null}
                </div>
              </StandardBorderedContainer>
              {!isProductSpecific ? (
                <div className="AlignedRowSpaced" style={{ gap: 14 }}>
                  <div className="ColumnNormal" style={{ flex: 1, gap: 4 }}>
                    <span className="bodyMedium">Refund Fees</span>
                    <span className="smallBodySubtext">
                      The processing fee ({ticketFeesDollars}
                      {ticket ? ` for this ${itemLabel.singular}` : ""}) cannot
                      be refunded by the platform. Check this box if you want to
                      refund the fee for the purchaser and subtract it from your
                      own account balance.
                    </span>
                  </div>
                  <CustomCheckbox
                    checked={refundFeesChecked}
                    onChange={() => setRefundFeesChecked(!refundFeesChecked)}
                    sx={{ padding: 0 }}
                    altColor={Colors.BLUE5}
                  />
                </div>
              ) : null}
            </div>
          )
        }
        fixedBottomContent={
          loading ? null : (
            <div className="ColumnNormal" style={{ gap: 14, paddingTop: 14 }}>
              {showRefundResult ? (
                <div style={{ paddingInline: 14 }}>
                  <RectangleButton
                    onPress={() => setModalVisible(false)}
                    buttonLabel="Done"
                    altColor={Colors.BLACK}
                    altTextColor={Colors.WHITE}
                    containerStyles={{ borderRadius: 14, paddingBlock: 14 }}
                  />
                </div>
              ) : (
                <>
                  <HorizontalDivider altMargin={0} />
                  <div
                    className="ColumnNormal"
                    style={{ paddingInline: 14, gap: 14 }}
                  >
                    <div className="ColumnNormal" style={{ gap: 7 }}>
                      <div className="AlignedRowSpaced" style={{ gap: 7 }}>
                        <span className="bodyMedium">Total Refund Amount</span>
                        <span className="bodyMedium">{ticketCostDollars}</span>
                      </div>
                      <span className="smallBodySubtext">
                        When making any refund through Markit you are subject to
                        Markit&apos;s{" "}
                        <span
                          onClick={() => window.open(MARKIT_TERMS_OF_USE)}
                          style={{ color: Colors.BLACK, cursor: "pointer" }}
                        >
                          terms of use
                        </span>
                        . All refunds are final and cannot be undone.
                      </span>
                    </div>
                    <div className="ColumnNormal" style={{ gap: 14 }}>
                      {!canRefund ? (
                        <span
                          className="smallBodySubtext"
                          style={{ textAlign: "center" }}
                        >
                          You do not have enough available funds on Stripe to
                          issue this refund.{" "}
                          <span
                            style={{
                              textDecoration: "underline",
                              color: Colors.BLACK,
                              fontWeight: 500,
                              cursor: "pointer",
                            }}
                            onClick={() => setAddFundsModal(true)}
                          >
                            Add Funds
                          </span>{" "}
                          and Refresh to see your updated balance.
                        </span>
                      ) : null}
                      <RectangleButton
                        buttonLabel={"Refund " + ticketCostDollars}
                        onPress={() => setConfirmRefundVisible(true)}
                        altColor={Colors.RED3}
                        altTextColor={Colors.WHITE}
                        altPaddingVert={14}
                        loading={loadingRefund}
                        disabled={!canRefund}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )
        }
        theme={theme}
        closeModal={() => setModalVisible(false)}
      />
      {addFundsModal ? (
        <AddStripeFundsModal
          isVisible={addFundsModal}
          setIsVisible={setAddFundsModal}
          defaultDepositAmount={(ticketCost - (currentBalance || 0)) / 100}
        />
      ) : null}
      <ConfirmActionModal
        heading={
          ticket
            ? `Are you sure you want to refund this ${itemLabel.singular} purchase?`
            : `Are you sure you want to refund all ${itemLabel.plural} purchases?`
        }
        subtext={"This cannot be undone."}
        confirmButtonText="Continue"
        hideModal={!confirmRefundVisible}
        setIsVisible={setConfirmRefundVisible}
        confirmOnPress={ticket ? refundPaymentOnPress : refundUsersOnPress}
      />
    </>
  );
};
